import { useContext } from "react";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";
import Head from "next/head";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import ConfigContext from "context/ConfigContext";
import FinderResultsContext from "context/FinderResultsContext";
import useDomain from "hooks/useDomain";
import { getDomainCountryCode } from "redux/selectors/config";
import { shouldNoIndexLocationPages } from "utils/browserUtils";
import { getFinderFilterForSeo } from "utils/finder/directusRequestUtils";
import { getPageMetaData } from "utils/seoMetaDataStrings";

const shouldNoIndexFilters = (query: NextParsedUrlQuery) =>
  (!!query?.filter && getFinderFilterForSeo(query.filter) === "") ||
  (!!query?.filter && query?.filter?.includes("delivery")) ||
  (Array.isArray(query?.filter) && query?.filter.length > 1);

const FinderHead: React.FC = () => {
  const { asPath, query } = useRouter();

  const domain = useDomain();
  const countryCode = useSelector(getDomainCountryCode);

  const { pageCount, finderLocation } = useContext(FinderResultsContext);
  const { FAQData, retailType, isLocationPage } = useContext(ConfigContext);

  const showTopLangAlternates = (asPath.match(/\//g) || []).length <= 1;

  const url = `${domain}${asPath.split("?")[0]}`;

  const { page = 1 } = query;
  const prevUrl = `${url}?page=${Number(page) - 1}`;
  const nextUrl = `${url}?page=${Number(page) + 1}`;

  const seoOverrides = FAQData.data
    ? {
        canonical: FAQData?.data?.canonical,
        metaDescription: FAQData?.data?.meta_description,
        metaTitle: FAQData?.data?.meta_title,
      }
    : null;

  const metadata = getPageMetaData(
    retailType,
    countryCode,
    isLocationPage ? finderLocation : undefined,
    query,
    seoOverrides || undefined
  );

  const pageQueryParam =
    Number(page) > 1
      ? metadata.canonical.includes("?")
        ? `&page=${page}`
        : `?page=${page}`
      : "";

  const isUS = countryCode === "US" || finderLocation?.countryCode === "US";
  const shouldShowAlternates = !isLocationPage && isUS;

  let noindexPage = false;

  if (
    shouldNoIndexLocationPages({
      domainCountryCode: countryCode,
      isLocationPage,
      locationCountryCode: finderLocation?.countryCode || countryCode,
      stateCode: finderLocation?.stateCode,
    }) ||
    // no-index all filters except black_owned, open_now, or medical and no-index if there are multiple filters
    shouldNoIndexFilters(query) ||
    // no-index if query params are location or zip
    asPath.includes("location") ||
    asPath.includes("zip")
  ) {
    noindexPage = true;
  }

  return (
    <Head>
      {noindexPage && <meta name="robots" content="noindex" key="noindex" />}
      {Number(page) > 1 && <link rel="prev" href={prevUrl} />}
      {Number(page) < pageCount && <link rel="next" href={nextUrl} />}
      <title>{`${metadata.title}${
        Number(page) > 1 ? ` | Page ${page}` : ""
      }`}</title>
      <link
        rel="canonical"
        href={`${domain}${metadata.canonical}${pageQueryParam}`}
      />
      {shouldShowAlternates && (
        <>
          <link
            rel="alternate"
            hrefLang={showTopLangAlternates ? "en-us" : undefined}
            href={`https://www.leafly.com${metadata.canonical}`}
          />
          <link
            rel="alternate"
            hrefLang={showTopLangAlternates ? "en-ca" : undefined}
            href={`https://www.leafly.ca${metadata.canonical}`}
          />
        </>
      )}
      <meta
        name="description"
        content={`${metadata.description}${
          Number(page) > 1 ? ` Page ${page}.` : ""
        }`}
      />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=yes"
      />
      <link rel="preconnect" href="https://maps.googleapis.com" />
    </Head>
  );
};

export default FinderHead;
